/**
 * CreateCategory - Create/Edit custom category form (WP-CAT-12)
 *
 * Form for creating new custom categories or editing existing ones.
 *
 * Features:
 * - Name, type, parent, icon, color fields
 * - Edit mode via URL param: /categories?tab=create&edit={categoryId}
 * - Validation feedback inline
 * - Success toast on creation/update
 * - Delete button for custom categories
 *
 * API:
 * - POST /categories (create)
 * - PATCH /categories/{id} (update)
 * - DELETE /categories/{id} (delete)
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import IconPicker from './IconPicker';
import ColorPicker from './ColorPicker';
import { toast } from 'sonner';
import { categoriesService } from '@/services/categories';
import { useCategoryPreferences } from '@/hooks/useCategoryPreferences';
import type { CategoryType, CategoryCreateRequest, CategoryUpdateRequest } from '@/types/category';

// ============================================================================
// Types
// ============================================================================

interface FormData {
  name: string;
  type: CategoryType;
  parentId: string;
  icon: string;
  color: string;
}

interface FormErrors {
  name?: string;
  type?: string;
  parentId?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function CreateCategory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const editId = searchParams.get('edit');
  const isEditMode = !!editId;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    type: 'expense',
    parentId: '',
    icon: '',
    color: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDirty, setIsDirty] = useState(false);

  // Fetch category data for edit mode
  const { data: editCategory, isLoading: isLoadingEdit, isError: isEditError } = useQuery({
    queryKey: ['categories', 'detail', editId],
    queryFn: () => categoriesService.getById(editId!),
    enabled: isEditMode,
  });

  // Fetch categories for parent selector
  const { data: categories } = useCategoryPreferences(true);

  // Populate form when editing
  useEffect(() => {
    if (editCategory) {
      setFormData({
        name: editCategory.name,
        type: editCategory.type,
        parentId: editCategory.parentId || '',
        icon: editCategory.icon || '',
        color: editCategory.color || '',
      });
      setIsDirty(false);
    }
  }, [editCategory]);

  // Reset form when switching modes
  useEffect(() => {
    if (!isEditMode) {
      setFormData({
        name: '',
        type: 'expense',
        parentId: '',
        icon: '',
        color: '',
      });
      setErrors({});
      setIsDirty(false);
    }
  }, [isEditMode]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CategoryCreateRequest) => categoriesService.create(data),
    onSuccess: () => {
      toast.success('Category created successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      // Reset form
      setFormData({
        name: '',
        type: 'expense',
        parentId: '',
        icon: '',
        color: '',
      });
      setIsDirty(false);
    },
    onError: (error: Error) => {
      toast.error('Failed to create category', {
        description: error.message,
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoryUpdateRequest }) =>
      categoriesService.update(id, data),
    onSuccess: () => {
      toast.success('Category updated successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsDirty(false);
    },
    onError: (error: Error) => {
      toast.error('Failed to update category', {
        description: error.message,
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesService.delete(id),
    onSuccess: () => {
      toast.success('Category deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      // Navigate back to manage tab
      setSearchParams({ tab: 'manage' });
    },
    onError: (error: Error) => {
      toast.error('Failed to delete category', {
        description: error.message,
      });
    },
  });

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle form field changes
  const handleChange = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setIsDirty(true);
      // Clear error for this field
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  // Handle form submit
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) return;

      if (isEditMode && editId) {
        const updateData: CategoryUpdateRequest = {
          name: formData.name,
          parentId: formData.parentId || undefined,
          icon: formData.icon || undefined,
          color: formData.color || undefined,
        };
        updateMutation.mutate({ id: editId, data: updateData });
      } else {
        const createData: CategoryCreateRequest = {
          name: formData.name,
          type: formData.type,
          parentId: formData.parentId || undefined,
          icon: formData.icon || undefined,
          color: formData.color || undefined,
        };
        createMutation.mutate(createData);
      }
    },
    [validateForm, isEditMode, editId, formData, updateMutation, createMutation]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    setSearchParams({ tab: 'manage' });
  }, [setSearchParams]);

  // Handle delete
  const handleDelete = useCallback(() => {
    if (editId && window.confirm('Are you sure you want to delete this category?')) {
      deleteMutation.mutate(editId);
    }
  }, [editId, deleteMutation]);

  // Get potential parent categories (same type, not self)
  const parentOptions = categories
    ?.filter((cat) => cat.type === formData.type && cat.id !== editId)
    .map((cat) => ({ value: cat.id, label: cat.name })) || [];

  // Loading state for edit mode
  if (isEditMode && isLoadingEdit) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Loading category...</p>
        </div>
      </Card>
    );
  }

  // Error state for edit mode
  if (isEditMode && isEditError) {
    return (
      <Card>
        <Alert variant="error" className="mb-4">
          Failed to load category. It may not exist or you may not have access.
        </Alert>
        <Button onClick={handleCancel} variant="outline">
          Go to Manage
        </Button>
      </Card>
    );
  }

  // Check if editing a system category
  if (isEditMode && editCategory?.isSystem) {
    return (
      <Card>
        <Alert variant="warning" className="mb-4">
          System categories cannot be edited.
        </Alert>
        <Button onClick={handleCancel} variant="outline">
          Go to Manage
        </Button>
      </Card>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Card>
      <CardHeader
        title={isEditMode ? 'Edit Category' : 'Create Custom Category'}
        description={
          isEditMode
            ? 'Update the details of your custom category.'
            : 'Create a new custom category for organizing transactions.'
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="category-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Groceries, Utilities, Income"
            maxLength={100}
            error={errors.name}
            aria-describedby={errors.name ? 'category-name-error' : undefined}
          />
          {errors.name && (
            <p id="category-name-error" className="mt-1 text-sm text-red-600">
              {errors.name}
            </p>
          )}
        </div>

        {/* Type (disabled in edit mode) */}
        <div>
          <Select
            id="category-type"
            label="Type *"
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            disabled={isEditMode}
            options={[
              { value: 'income', label: 'Income' },
              { value: 'expense', label: 'Expense' },
              { value: 'transfer', label: 'Transfer' },
            ]}
            hint={isEditMode ? 'Category type cannot be changed after creation.' : undefined}
          />
        </div>

        {/* Parent Category */}
        <div>
          <Select
            id="category-parent"
            label="Parent Category"
            value={formData.parentId}
            onChange={(e) => handleChange('parentId', e.target.value)}
            options={[{ value: '', label: 'None (Top Level)' }, ...parentOptions]}
            hint="Optionally nest this category under another."
          />
        </div>

        {/* Icon */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Icon
          </label>
          <IconPicker
            value={formData.icon}
            onChange={(icon) => handleChange('icon', icon)}
          />
        </div>

        {/* Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Color
          </label>
          <ColorPicker
            value={formData.color}
            onChange={(color) => handleChange('color', color)}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {isEditMode ? 'Save Changes' : 'Create Category'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {isEditMode && !editCategory?.isSystem && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting || deleteMutation.isPending}
              className="ml-auto"
            >
              Delete Category
            </Button>
          )}
        </div>

        {/* Unsaved changes warning */}
        {isDirty && (
          <p className="text-sm text-amber-600">
            You have unsaved changes.
          </p>
        )}
      </form>
    </Card>
  );
}
